import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { RevealPackCardsDialogFromReward } from 'components/commonCard/RevealPackCardsDialogFromReward';

import type {
  SeriesClaimDailyPackMutation,
  SeriesClaimDailyPackMutationVariables,
  SeriesDailyClaimModule_task,
} from './__generated__/index.graphql';

const Wrapper = styled(ButtonBase)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: var(--double-unit);
`;

const GlowCircle = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  border-radius: 50%;
  background: rgba(14, 14, 14, 0.54);
  backdrop-filter: blur(20px);
  box-shadow:
    0px 0px 20px 0px rgba(0, 243, 235, 0.6),
    inset 0px 0px 32px 0px rgba(0, 243, 235, 0.3),
    inset 0px 0px 8px 0px rgba(0, 243, 235, 0.4),
    inset 0px 0px 4px 1px rgba(0, 243, 235, 0.8);
  position: relative;
`;

const PackImage = styled.img`
  width: 32px;
  height: 48px;
  object-fit: contain;
`;

const FreeBadge = styled(Chip)`
  margin-top: calc(-1 * var(--unit));
`;

const NotificationDot = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  right: var(--half-unit);
`;

const SERIES_CLAIM_DAILY_PACK_MUTATION = gql`
  mutation SeriesClaimDailyPackMutation($taskId: ID!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
      }
      rewards {
        id
        ...RevealPackCardsDialogFromReward_anyRewardInterface
      }
      errors {
        message
        code
      }
    }
  }
  ${RevealPackCardsDialogFromReward.fragments.anyRewardInterface}
` as TypedDocumentNode<
  SeriesClaimDailyPackMutation,
  SeriesClaimDailyPackMutationVariables
>;

type Props = {
  task?: SeriesDailyClaimModule_task;
  refetch: () => Promise<unknown>;
};

export const SeriesDailyClaimModule = ({ task, refetch }: Props) => {
  const { isIosApp, isAndroidApp, postMessage } = useIsMobileApp();

  const {
    flags: { disableNativeIOSPackOpening = false },
  } = useFeatureFlags();
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();
  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [claimMutationData, setClaimMutationData] =
    useState<SeriesClaimDailyPackMutation['claimTask']>();
  const [claimTask] = useMutation(SERIES_CLAIM_DAILY_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const isClaimable =
    task && task?.aasmState !== ManagerProgressionTaskState.CLAIMED;
  const pack = task?.rewardConfigs.find(
    isTypeFn('CardPacksRewardConfig')
  )?.cardPack;

  const onClaim = async () => {
    if (!isClaimable) {
      return;
    }
    if (isIosApp && !disableNativeIOSPackOpening) {
      // on ios the message opens the native pack opening dialog
      postMessage('claimFreeCardPack', {
        taskId: task.id,
      });
      return;
    }
    setPackDialogOpen(true);
    const { data } = await claimTask({
      variables: {
        taskId: task.id,
      },
    });
    if (isAndroidApp) {
      // on android the message refreshes the market red dot, pack opening is still handled by the web
      postMessage('claimFreeCardPack', {
        taskId: task.id,
      });
    }
    setClaimMutationData(data?.claimTask);
  };

  const reward = claimMutationData?.rewards?.at(0);

  return (
    <>
      <Wrapper onClick={onClaim} disabled={!isClaimable}>
        <GlowCircle>
          {!!pack?.pictureUrl && <PackImage src={pack.pictureUrl} alt="" />}
          {!!isClaimable && (
            <NotificationDot>
              <LiveDot animate size="md" />
            </NotificationDot>
          )}
        </GlowCircle>
        <FreeBadge>
          <LabelS bold>
            {isClaimable ? (
              <FormattedMessage
                id="FreePackButton.free"
                defaultMessage="Free"
              />
            ) : (
              dailyTasksExpiresAt && (
                <TimeLeft time={dailyTasksExpiresAt} onEnded={refetch} />
              )
            )}
          </LabelS>
        </FreeBadge>
      </Wrapper>
      {!!reward && (
        <RevealPackCardsDialogFromReward
          open={packDialogOpen}
          reward={claimMutationData?.rewards?.at(0)}
          onClose={() => {
            setPackDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

SeriesDailyClaimModule.fragments = {
  task: gql`
    fragment SeriesDailyClaimModule_task on Task {
      id
      aasmState
      rewardConfigs {
        id
        ... on CardPacksRewardConfig {
          id
          cardPack {
            slug
            pictureUrl
          }
        }
      }
    }
  ` as TypedDocumentNode<SeriesDailyClaimModule_task>,
};
